"use client"

import { BlockchainFilterView } from "@/app/blockchains/filters/blockchain-filter"
import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import {
  ToggleGroup,
  ToggleGroupItem,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { memo, useCallback, useRef } from "react"

interface SearchBlockchainFiltersProps {
  blockchains: BlockchainFilter[]
  selectedBlockchain: BlockchainFilter
  onSelect: (blockchain: BlockchainFilter) => void
  className?: string
}

function areEqual(
  prevProps: SearchBlockchainFiltersProps,
  nextProps: SearchBlockchainFiltersProps,
) {
  return (
    prevProps.selectedBlockchain === nextProps.selectedBlockchain &&
    prevProps.onSelect === nextProps.onSelect &&
    prevProps.className === nextProps.className &&
    prevProps.blockchains.length === nextProps.blockchains.length &&
    prevProps.blockchains.every((b, i) => b === nextProps.blockchains[i])
  )
}

export const SearchBlockchainFilters = memo(function SearchBlockchainFilters({
  blockchains,
  selectedBlockchain,
  onSelect,
  className,
}: SearchBlockchainFiltersProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const scrollLeftRef = useRef(0)
  const hasMovedRef = useRef(false)

  const onChange = useCallback(
    (value: string) => {
      if (value && value !== selectedBlockchain) {
        onSelect(value as BlockchainFilter)
      }
    },
    [selectedBlockchain, onSelect],
  )

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return
    isDraggingRef.current = true
    hasMovedRef.current = false
    startXRef.current = e.pageX - scrollContainerRef.current.offsetLeft
    scrollLeftRef.current = scrollContainerRef.current.scrollLeft
    scrollContainerRef.current.style.cursor = "grabbing"
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollContainerRef.current.offsetLeft
    const walk = (x - startXRef.current) * 2

    // Only consider it a drag if moved more than 5px
    if (Math.abs(walk) > 5) {
      hasMovedRef.current = true
      scrollContainerRef.current.scrollLeft = scrollLeftRef.current - walk
    }
  }, [])

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = "grab"
    }
    // Reset hasMoved after a short delay to allow click handler to check it
    setTimeout(() => {
      hasMovedRef.current = false
    }, 0)
  }, [])

  const handleMouseLeave = useCallback(() => {
    isDraggingRef.current = false
    hasMovedRef.current = false
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = "grab"
    }
  }, [])

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (hasMovedRef.current) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  return (
    <div
      ref={scrollContainerRef}
      className={cn(
        "scrollbar-thin flex w-full cursor-grab select-none overflow-x-auto",
        className,
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <ToggleGroup
        type="single"
        value={selectedBlockchain}
        data-testid="search-blockchain-filters"
        onValueChange={onChange}
        className="flex-none"
      >
        <div className="flex items-center gap-x-2 px-4 py-3">
          {blockchains.map((blockchain) => (
            <ToggleGroupItem
              key={blockchain}
              value={blockchain}
              disabled={false}
              className="shrink-0 basis-auto transition-all hover:bg-foreground/[0.06] hover:text-foreground/100"
            >
              <BlockchainFilterView
                blockchain={blockchain}
                isCompact={true}
                isSelected={selectedBlockchain === blockchain}
              />
            </ToggleGroupItem>
          ))}
        </div>
      </ToggleGroup>
    </div>
  )
}, areEqual)
